import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import {
  Assessment,
  AssessmentMetadata,
  Stack,
  BusinessFunction,
  Tool,
  ViewMode,
  DisplayMode,
  CMMIRating,
} from "../types";
import {
  createAssessment,
  createFunction,
  createTool,
} from "../utils/defaults";

interface AppState {
  // Data
  assessment: Assessment;
  // UI State
  viewMode: ViewMode;
  displayMode: DisplayMode;
  selectedStackId: string | null;
  selectedFunctionId: string | null;
  selectedToolId: string | null;
  isLoading: boolean;
  error: string | null;
}

interface AppActions {
  // Assessment actions
  setAssessment: (assessment: Assessment) => void;
  setTitle: (title: string) => void;
  setMetadata: (metadata: AssessmentMetadata | undefined) => void;
  resetAssessment: () => void;

  // Stack actions
  addStack: (stack: Stack) => void;
  updateStack: (stackId: string, updates: Partial<Stack>) => void;
  deleteStack: (stackId: string) => void;
  reorderStacks: (fromIndex: number, toIndex: number) => void;

  // Function actions
  addFunction: (stackId: string, func: BusinessFunction) => void;
  updateFunction: (
    stackId: string,
    funcId: string,
    updates: Partial<BusinessFunction>,
  ) => void;
  deleteFunction: (stackId: string, funcId: string) => void;
  moveFunction: (
    fromStackId: string,
    toStackId: string,
    funcId: string,
    toIndex: number,
  ) => void;

  // Tool actions
  addTool: (stackId: string, funcId: string, tool: Tool) => void;
  updateTool: (
    stackId: string,
    funcId: string,
    toolId: string,
    updates: Partial<Tool>,
  ) => void;
  deleteTool: (stackId: string, funcId: string, toolId: string) => void;
  moveTool: (
    fromStackId: string,
    fromFuncId: string,
    toStackId: string,
    toFuncId: string,
    toolId: string,
    toIndex: number,
  ) => void;

  // Quick actions
  addNewFunction: (stackId: string) => void;
  addNewTool: (stackId: string, funcId: string) => void;
  updateToolRating: (
    stackId: string,
    funcId: string,
    toolId: string,
    rating: CMMIRating,
  ) => void;

  // UI actions
  setViewMode: (mode: ViewMode) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  setSelectedStack: (stackId: string | null) => void;
  setSelectedFunction: (funcId: string | null) => void;
  setSelectedTool: (toolId: string | null) => void;
  setError: (error: string | null) => void;
  clearSelection: () => void;
}

type AppStore = AppState & AppActions;

const initialState: AppState = {
  assessment: createAssessment("New Assessment"),
  viewMode: "All",
  displayMode: "stacks",
  selectedStackId: null,
  selectedFunctionId: null,
  selectedToolId: null,
  isLoading: false,
  error: null,
};

export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        // Assessment actions
        setAssessment: (assessment) =>
          set({ assessment }, false, "setAssessment"),

        setTitle: (title) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                title,
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "setTitle",
          ),

        setMetadata: (metadata) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                metadata,
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "setMetadata",
          ),

        resetAssessment: () =>
          set(
            { assessment: createAssessment("New Assessment") },
            false,
            "resetAssessment",
          ),

        // Stack actions
        addStack: (stack) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: [...state.assessment.stacks, stack],
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "addStack",
          ),

        updateStack: (stackId, updates) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId ? { ...s, ...updates } : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "updateStack",
          ),

        deleteStack: (stackId) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.filter((s) => s.id !== stackId),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "deleteStack",
          ),

        reorderStacks: (fromIndex, toIndex) =>
          set(
            (state) => {
              const stacks = [...state.assessment.stacks];
              const [moved] = stacks.splice(fromIndex, 1);
              stacks.splice(toIndex, 0, moved);
              return {
                assessment: {
                  ...state.assessment,
                  stacks,
                  lastEdited: new Date().toISOString(),
                },
              };
            },
            false,
            "reorderStacks",
          ),

        // Function actions
        addFunction: (stackId, func) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId
                    ? { ...s, functions: [...s.functions, func] }
                    : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "addFunction",
          ),

        updateFunction: (stackId, funcId, updates) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId
                    ? {
                        ...s,
                        functions: s.functions.map((f) =>
                          f.id === funcId ? { ...f, ...updates } : f,
                        ),
                      }
                    : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "updateFunction",
          ),

        deleteFunction: (stackId, funcId) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId
                    ? {
                        ...s,
                        functions: s.functions.filter((f) => f.id !== funcId),
                      }
                    : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "deleteFunction",
          ),

        moveFunction: (fromStackId, toStackId, funcId, toIndex) =>
          set(
            (state) => {
              const stacks = state.assessment.stacks.map((s) => ({
                ...s,
                functions: [...s.functions],
              }));
              const fromStack = stacks.find((s) => s.id === fromStackId);
              const toStack = stacks.find((s) => s.id === toStackId);
              if (!fromStack || !toStack) return state;

              const funcIndex = fromStack.functions.findIndex(
                (f) => f.id === funcId,
              );
              if (funcIndex === -1) return state;

              const [func] = fromStack.functions.splice(funcIndex, 1);
              toStack.functions.splice(toIndex, 0, func);

              return {
                assessment: {
                  ...state.assessment,
                  stacks,
                  lastEdited: new Date().toISOString(),
                },
              };
            },
            false,
            "moveFunction",
          ),

        // Tool actions
        addTool: (stackId, funcId, tool) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId
                    ? {
                        ...s,
                        functions: s.functions.map((f) =>
                          f.id === funcId
                            ? { ...f, tools: [...f.tools, tool] }
                            : f,
                        ),
                      }
                    : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "addTool",
          ),

        updateTool: (stackId, funcId, toolId, updates) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId
                    ? {
                        ...s,
                        functions: s.functions.map((f) =>
                          f.id === funcId
                            ? {
                                ...f,
                                tools: f.tools.map((t) =>
                                  t.id === toolId ? { ...t, ...updates } : t,
                                ),
                              }
                            : f,
                        ),
                      }
                    : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "updateTool",
          ),

        deleteTool: (stackId, funcId, toolId) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId
                    ? {
                        ...s,
                        functions: s.functions.map((f) =>
                          f.id === funcId
                            ? {
                                ...f,
                                tools: f.tools.filter((t) => t.id !== toolId),
                              }
                            : f,
                        ),
                      }
                    : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "deleteTool",
          ),

        moveTool: (
          fromStackId,
          fromFuncId,
          toStackId,
          toFuncId,
          toolId,
          toIndex,
        ) =>
          set(
            (state) => {
              const stacks = state.assessment.stacks.map((s) => ({
                ...s,
                functions: s.functions.map((f) => ({
                  ...f,
                  tools: [...f.tools],
                })),
              }));

              const fromStack = stacks.find((s) => s.id === fromStackId);
              const toStack = stacks.find((s) => s.id === toStackId);
              if (!fromStack || !toStack) return state;

              const fromFunc = fromStack.functions.find(
                (f) => f.id === fromFuncId,
              );
              const toFunc = toStack.functions.find((f) => f.id === toFuncId);
              if (!fromFunc || !toFunc) return state;

              const toolIndex = fromFunc.tools.findIndex(
                (t) => t.id === toolId,
              );
              if (toolIndex === -1) return state;

              const [tool] = fromFunc.tools.splice(toolIndex, 1);
              toFunc.tools.splice(toIndex, 0, tool);

              return {
                assessment: {
                  ...state.assessment,
                  stacks,
                  lastEdited: new Date().toISOString(),
                },
              };
            },
            false,
            "moveTool",
          ),

        // Quick actions
        addNewFunction: (stackId) =>
          set(
            (state) => {
              const newFunc = createFunction();
              return {
                assessment: {
                  ...state.assessment,
                  stacks: state.assessment.stacks.map((s) =>
                    s.id === stackId
                      ? { ...s, functions: [...s.functions, newFunc] }
                      : s,
                  ),
                  lastEdited: new Date().toISOString(),
                },
                selectedFunctionId: newFunc.id,
              };
            },
            false,
            "addNewFunction",
          ),

        addNewTool: (stackId, funcId) =>
          set(
            (state) => {
              const newTool = createTool();
              return {
                assessment: {
                  ...state.assessment,
                  stacks: state.assessment.stacks.map((s) =>
                    s.id === stackId
                      ? {
                          ...s,
                          functions: s.functions.map((f) =>
                            f.id === funcId
                              ? { ...f, tools: [...f.tools, newTool] }
                              : f,
                          ),
                        }
                      : s,
                  ),
                  lastEdited: new Date().toISOString(),
                },
                selectedToolId: newTool.id,
              };
            },
            false,
            "addNewTool",
          ),

        updateToolRating: (stackId, funcId, toolId, rating) =>
          set(
            (state) => ({
              assessment: {
                ...state.assessment,
                stacks: state.assessment.stacks.map((s) =>
                  s.id === stackId
                    ? {
                        ...s,
                        functions: s.functions.map((f) =>
                          f.id === funcId
                            ? {
                                ...f,
                                tools: f.tools.map((t) =>
                                  t.id === toolId
                                    ? { ...t, currentRating: rating }
                                    : t,
                                ),
                              }
                            : f,
                        ),
                      }
                    : s,
                ),
                lastEdited: new Date().toISOString(),
              },
            }),
            false,
            "updateToolRating",
          ),

        // UI actions
        setViewMode: (mode) => set({ viewMode: mode }, false, "setViewMode"),

        setDisplayMode: (mode) =>
          set({ displayMode: mode }, false, "setDisplayMode"),

        setSelectedStack: (stackId) =>
          set({ selectedStackId: stackId }, false, "setSelectedStack"),

        setSelectedFunction: (funcId) =>
          set({ selectedFunctionId: funcId }, false, "setSelectedFunction"),

        setSelectedTool: (toolId) =>
          set({ selectedToolId: toolId }, false, "setSelectedTool"),

        setError: (error) => set({ error }, false, "setError"),

        clearSelection: () =>
          set(
            {
              selectedStackId: null,
              selectedFunctionId: null,
              selectedToolId: null,
            },
            false,
            "clearSelection",
          ),
      }),
      {
        name: "profitstacks-storage",
        partialize: (state) => ({
          assessment: state.assessment,
          viewMode: state.viewMode,
        }),
      },
    ),
    { name: "ProfitStacks" },
  ),
);
